import React, { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBackward, faFastBackward, faFastForward, faForward } from "@fortawesome/free-solid-svg-icons";
import { Link, To } from "react-router-dom";
import { Button, ButtonGroup } from "@mui/material";

export const FIRST_PAGE = 1;

const PageLink = ({ disabled, children, to }: { disabled: boolean; children: ReactNode; to: To }) => {
    return (
        <Button
            disabled={disabled}
            color="primary"
            component={disabled ? "button" : Link}
            to={disabled ? undefined : to}
        >
            {children}
        </Button>
    );
};

interface PaginatorProps {
    uri: URL;
    maxPage: number;
    numOfPages?: number;
}

export default class Paginator extends React.Component<PaginatorProps> {
    public static defaultProps = {
        numOfPages: 10,
    };

    constructURI(page: number, maxPage: number): To {
        if (page < FIRST_PAGE) page = FIRST_PAGE;
        if (page > maxPage) page = maxPage;
        const uri = new URL(this.props.uri);
        uri.searchParams.set("page", page.toString());
        return {
            pathname: uri.pathname,
            search: uri.search,
        };
    }

    /**
     * Calculates and returns the range of numbers that should be displayed
     */
    getPageNumberRange() {
        let currPage = parseInt(this.props.uri.searchParams.get("page"));
        let { numOfPages, maxPage } = this.props;

        if (maxPage < numOfPages) numOfPages = maxPage;

        if (isNaN(currPage)) currPage = FIRST_PAGE;

        let startFromPage = FIRST_PAGE;
        let printTillPage = numOfPages;

        // Current page is in the middle range. The displayed page numbers need to be shifted
        if (currPage > numOfPages / 2) {
            startFromPage = currPage - numOfPages / 2;
            printTillPage = Math.ceil(currPage + (numOfPages / 2 - 1));
        }
        // Almost at the end. Adjust the starting number to print the correct number of pages
        if (currPage > maxPage - numOfPages / 2 && maxPage - numOfPages / 2 > 0) {
            startFromPage = currPage - (numOfPages - 1 - (maxPage - currPage));
        }

        return [currPage, startFromPage, printTillPage, maxPage];
    }

    /**
     * Builds and returns a list containing all the navigation Links
     */
    getLinks() {
        let [currPage, startFromPage, printTillPage, maxPage] = this.getPageNumberRange();
        let pages = [];

        pages.push(...this.getLeftNavigators(currPage, maxPage));
        pages.push(...this.getNumberedNavigators(currPage, startFromPage, maxPage, printTillPage));
        pages.push(...this.getRightNavigators(currPage, maxPage));

        return pages;
    }

    /**
     * Returns the "First page" and "previous page" Links"
     */
    getLeftNavigators(currPage: number, maxPage: number) {
        let cssClass = "";
        let disabled = false;
        if (currPage == FIRST_PAGE) {
            cssClass = "disabled";
            disabled = true;
        }

        return [
            <PageLink disabled={disabled} to={this.constructURI(FIRST_PAGE, maxPage)}>
                <FontAwesomeIcon icon={faFastBackward} />
            </PageLink>,
            <PageLink disabled={disabled} to={this.constructURI(currPage - 1, maxPage)}>
                <FontAwesomeIcon icon={faBackward} />
            </PageLink>,
        ];
    }

    /**
     * Returns the "next page" and "last page" Links
     */
    getRightNavigators(currPage: number, maxPage: number) {
        let cssClass = "";
        let disabled = false;
        if (currPage == maxPage || maxPage === 0) {
            cssClass = "disabled";
            disabled = true;
        }

        return [
            <PageLink disabled={disabled} to={this.constructURI(currPage + 1, maxPage)}>
                <FontAwesomeIcon icon={faForward} />
            </PageLink>,
            <PageLink disabled={disabled} to={this.constructURI(maxPage, maxPage)}>
                <FontAwesomeIcon icon={faFastForward} />
            </PageLink>,
        ];
    }

    /**
     * Returns the numbered Links
     */
    getNumberedNavigators(currPage: number, startFromPage: number, maxPage: number, printTillPage: number) {
        let pages = [];
        for (let i = startFromPage; i <= maxPage && i <= printTillPage; i++) {
            let currClass = "";
            let disabled = false;
            if (i == currPage) {
                currClass = "active";
                disabled = true;
            }
            pages.push(
                <PageLink disabled={disabled} to={this.constructURI(i, maxPage)}>
                    {i}
                </PageLink>,
            );
        }
        return pages;
    }

    render() {
        let pages = this.getLinks();

        return (
            <ButtonGroup variant="outlined" color="primary">
                {...pages}
            </ButtonGroup>
        );
    }
}
